/**
 * Store Helper Utilities - 2025 Best Practices
 * Reusable utilities for Pinia stores
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue'

/**
 * Create async state management helpers
 */
export function useAsyncState<T = unknown>(
  initialValue: T | null = null
) {
  const data = ref<T | null>(initialValue)
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)

  const execute = async (asyncFn: () => Promise<T>): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null
      const result = await asyncFn()
      data.value = result
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred'
      return null
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    data.value = initialValue
    loading.value = false
    error.value = null
  }

  return {
    data,
    loading,
    error,
    execute,
    reset
  }
}

/**
 * Create localStorage persistence helpers
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  serializer = JSON
): {
  value: Ref<T | null>
  save: () => void
  load: () => void
  remove: () => void
} {
  const value = ref(defaultValue) as Ref<T | null>

  const save = () => {
    try {
      localStorage.setItem(key, serializer.stringify(value.value))
    } catch (error) {
      console.warn(`Failed to save ${key} to localStorage:`, error)
    }
  }

  const load = () => {
    try {
      const stored = localStorage.getItem(key)
      if (stored !== null) {
        value.value = serializer.parse(stored)
      }
    } catch (error) {
      console.warn(`Failed to load ${key} from localStorage:`, error)
      value.value = defaultValue
    }
  }

  const remove = () => {
    try {
      localStorage.removeItem(key)
      value.value = defaultValue
    } catch (error) {
      console.warn(`Failed to remove ${key} from localStorage:`, error)
    }
  }

  // Load initial value
  load()

  return {
    value,
    save,
    load,
    remove
  }
}

/**
 * Create validation helpers
 */
export function useValidation<T extends Record<string, unknown>>(
  data: Ref<T>,
  rules: Record<keyof T, (value: unknown) => string | null>
): {
  errors: ComputedRef<Partial<Record<keyof T, string>>>
  isValid: ComputedRef<boolean>
  validate: () => boolean
  validateField: (field: keyof T) => string | null
} {
  const errors = computed(() => {
    const result: Partial<Record<keyof T, string>> = {}
    for (const [field, rule] of Object.entries(rules)) {
      const error = rule(data.value[field])
      if (error) {
        result[field as keyof T] = error
      }
    }
    return result
  })

  const isValid = computed(() => Object.keys(errors.value).length === 0)

  const validate = () => isValid.value

  const validateField = (field: keyof T) => {
    const rule = rules[field]
    return rule ? rule(data.value[field]) : null
  }

  return {
    errors,
    isValid,
    validate,
    validateField
  }
}

/**
 * Create store helpers factory
 */
export function createStoreHelpers() {
  return {
    useAsyncState,
    useLocalStorage,
    useValidation
  }
}

/**
 * Create debounced action helper
 */
export function useDebouncedAction<T extends unknown[]>(
  action: (...args: T) => void,
  delay: number = 300
): (...args: T) => void {
  let timeoutId: number | null = null

  return (...args: T) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
    timeoutId = window.setTimeout(() => {
      action(...args)
      timeoutId = null
    }, delay)
  }
}

/**
 * Create retry helper
 */
export function useRetry<T>(
  maxRetries: number = 3,
  delay: number = 1000
): {
  execute: (fn: () => Promise<T>) => Promise<T>
  retryCount: Ref<number>
  isRetrying: Ref<boolean>
} {
  const retryCount = ref(0)
  const isRetrying = ref(false)

  const execute = async (fn: () => Promise<T>): Promise<T> => {
    retryCount.value = 0
    isRetrying.value = false

    const attempt = async (): Promise<T> => {
      try {
        const result = await fn()
        retryCount.value = 0
        isRetrying.value = false
        return result
      } catch (error) {
        retryCount.value++

        if (retryCount.value >= maxRetries) {
          isRetrying.value = false
          throw error
        }

        isRetrying.value = true
        await new Promise(resolve => setTimeout(resolve, delay * retryCount.value))
        return attempt()
      }
    }

    return attempt()
  }

  return {
    execute,
    retryCount,
    isRetrying
  }
}
